import { BrewManError } from "@/app/errors";
import errorLoggerService from "@/app/errors/errorLoggerService";
import { enumFromJson, isEnum } from "@/app/services/baseEnumType";
import Guid from "@/app/types/common/guid";
import type { NamedModelObject } from "@/app/namedModelObjectStore";
import { Instant, LocalDate, LocalDateTime, LocalTime } from "@js-joda/core";

/**
 * Provides a uniform way to serialize and deserialize objects with non diverging types
 */
export function useReviverReplacer(namedModelObjects: NamedModelObject[] = []) {
  return {
    reviver: createJsonParseReviver(namedModelObjects),
    replacer: createJsonStringifyReplacer(namedModelObjects),
  };
}

export function createJsonParseReviver(
  namedModelObjects: NamedModelObject[] = []
) {
  return (key: string, value: any) => {
    const reviveType = value?.reviveType ?? value?.type ?? value?.modelType; // @TODO: [BREW-6555] Remove modelType and type from non NamedModelObject types

    if (value && reviveType && value.value) {
      switch (reviveType) {
        case "GUID":
          return Guid.parseOrEmpty(value.value);
        case "BMO":
          return value.value;
        case "ENUM":
          // @FIXME: [BREW-6536] Will not be revived as the same instance of the enum
          return enumFromJson(value);
        case "LOCALDATE":
          return LocalDate.parse(value.value);
        case "LOCALTIME":
          return LocalTime.parse(value.value);
        case "LOCALDATETIME":
          return LocalDateTime.parse(value.value);
        case "INSTANT":
          return Instant.parse(value.value);
        case "MAP":
          return new Map(value.value);
        case "BMOTYPED_JSON":
          return deserializeBmoJson(namedModelObjects, value);
        case "BMOTYPED":
          // Could not successfully deserialize the object
          errorLoggerService.logError(
            `Could not deserialize Legacy Proto value of type ${value.modelType}`
          );
          return null;
        default:
          if (value?.reviveType) {
            // [BREW-6555] remove this wrapping if after all types have been migrated
            errorLoggerService.logError(
              "Unknown type in json parse reviver" + reviveType
            );
          }
          return value;
      }
    }
    return value;
  };
}

function deserializeBmoJson(
  namedModelObjects: NamedModelObject[],
  value: { modelType: string; value: string }
) {
  const foundModelObject = namedModelObjects.find(
    ({ objectTypeName }) => objectTypeName === value.modelType
  );

  if (foundModelObject) {
    return new foundModelObject.objectJsonClass(value.value);
  } else {
    throw new BrewManError(
      `Could not deserialize JSON value of type ${value.modelType}`
    );
  }
}

export function createJsonStringifyReplacer(
  namedModelObjects: NamedModelObject[] = []
) {
  /**
   * @warning Runs recursively on all properties of an object and replaces any
   */
  return function (this: any, key: string, value: any) {
    // This seems a small bug - if value is a string representation of a guid it will incorrectly inflate as a guid not a string.
    if (value && value.value && Guid.canParse(value.value.toString())) {
      return { reviveType: "GUID", value: value.value };
    }
    if (isBmoObject(value)) {
      // Warning: The replacer will run recursively on all properties of this object and replace any sub objects
      return bmoObjectSerialization(value, namedModelObjects);
    }

    if (value && isEnum(value)) {
      return {
        reviveType: "ENUM",
        value: value.value,
        key: value.key,
        translationKey: value.translationKey,
        enumName: value.constructor.name,
      };
    }

    // Check if an instance of a known type
    const objectValue = this[key];
    if (objectValue) {
      let objectTypeName;

      if (objectValue instanceof Map) {
        return {
          reviveType: "MAP",
          value: Array.from(value),
        };
      }

      if (objectValue instanceof LocalDate) {
        objectTypeName = "LOCALDATE";
      } else if (objectValue instanceof LocalTime) {
        objectTypeName = "LOCALTIME";
      } else if (objectValue instanceof LocalDateTime) {
        objectTypeName = "LOCALDATETIME";
      } else if (objectValue instanceof Instant) {
        objectTypeName = "INSTANT";
      }

      if (objectTypeName) {
        return {
          reviveType: objectTypeName,
          value,
        };
      }
    }

    return value;
  };
}

function isBmoObject(value: any) {
  return value && value.validate;
}

function bmoObjectSerialization(
  value: any,
  namedModelObjects: NamedModelObject[] = []
) {
  const modelObjectType = namedModelObjects.find(
    ({ objectJsonClass }) => objectJsonClass.name === value.constructor.name
  );

  if (modelObjectType) {
    return {
      reviveType: "BMOTYPED_JSON",
      modelType: modelObjectType.objectTypeName,
      value: value.toJsonSerializedObject(),
    };
  }

  // BaseModelObjects contain private fields with computed getters..
  // move the private "_" prefixed fields up one
  return { reviveType: "BMO", value: removeUnderscoreRecursive(value) };
}

function removeUnderscoreRecursive(obj: Object) {
  const newObj = {} as any;
  for (let [key, value] of Object.entries(obj)) {
    if (key.startsWith("_") && key.length > 1) {
      newObj[key.slice(1)] = value;
    } else {
      newObj[key] = value;
    }
  }
  return newObj;
}
